import { format, parseISO } from 'date-fns';

// The timezone we'll use consistently throughout the app
export const TIMEZONE = 'America/New_York'; // Eastern Time

/**
 * Format a date string to Eastern Time with timezone indicator
 * @param dateString ISO date string
 * @returns Formatted date string in Eastern Time
 */
export const formatToEasternTime = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(date);
};

/**
 * Convert a Date object to Eastern Time
 * @param date Date object
 * @returns Date object adjusted to Eastern Time
 */
export const toEasternTime = (date: Date): Date => {
  return new Date(
    new Intl.DateTimeFormat('en-US', {
      timeZone: TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(date)
  );
};

/**
 * Convert a Date object from Eastern Time to UTC
 * @param easternDate Date object in Eastern Time
 * @returns Date object in UTC
 */
export const fromEasternTimeToUTC = (easternDate: Date): Date => {
  const easternDateObj = new Date(easternDate.toLocaleString('en-US', { timeZone: TIMEZONE }));
  const diff = easternDate.getTime() - easternDateObj.getTime();
  return new Date(easternDate.getTime() + diff);
};

/**
 * Convert an ISO date string to an Eastern Time Date object
 * @param dateString ISO date string
 * @returns Date object in Eastern Time
 */
export const isoToEasternDate = (dateString: string): Date => {
  const date = new Date(dateString);
  return new Date(date.toLocaleString('en-US', { timeZone: TIMEZONE }));
};

/**
 * Format a Date with custom format in Eastern Time
 * @param date Date object
 * @param formatStr Date format string
 * @returns Formatted date string
 */
export const formatDateInET = (date: Date, formatStr: string): string => {
  const easternDate = new Date(date.toLocaleString('en-US', { timeZone: TIMEZONE }));
  return format(easternDate, formatStr);
};

/**
 * Treats a browser-local date as if it were an Eastern Time date
 * This is important when a user selects a time in the date picker
 * @param localDate Date object from browser (like from date picker)
 * @returns Date object correctly adjusted for UTC storage
 */
export const treatLocalAsEastern = (localDate: Date): Date => {
  // The user selected a time in the date picker, which they interpret as Eastern Time
  // We need to create a date that, when formatted in ET, shows that same time
  
  // Get the current date in ET to determine if we're in EDT or EST
  const nowInET = new Date().toLocaleString('en-US', { timeZone: TIMEZONE });
  const etDate = new Date(nowInET);
  
  // Determine if we're in EDT or EST based on offset
  const stdTimezoneOffset = Math.max(
    new Date(etDate.getFullYear(), 0, 1).getTimezoneOffset(),
    new Date(etDate.getFullYear(), 6, 1).getTimezoneOffset()
  );
  const isDST = etDate.getTimezoneOffset() < stdTimezoneOffset;
  
  // Get the hour offset (either -4 or -5 hours from UTC)
  const hourOffset = isDST ? 4 : 5;
  
  // Get the local components from the date picker selection
  const h = localDate.getHours();
  const m = localDate.getMinutes();
  const s = localDate.getSeconds();
  const year = localDate.getFullYear();
  const month = localDate.getMonth();
  const day = localDate.getDate();
  
  // Create a UTC date that, when interpreted as ET, will show the time the user selected
  // We do this by adding the ET offset to the hours
  return new Date(Date.UTC(year, month, day, h + hourOffset, m, s));
};

// Format a date using date-fns (when timezone conversion is not needed)
export const formatDate = (date: Date, formatString: string) => {
  return format(date, formatString);
}; 